import {
  useQuery,
  useInfiniteQuery,
  useMutation,
  useQueryClient,
  UseQueryOptions,
} from '@tanstack/react-query';
import { api } from './api';
import { useAuthStore } from '@/store/auth';

const scopeParam = () => {
  const s = useAuthStore.getState().scope;
  return s === 'me' ? undefined : s;
};

export const useMe = () =>
  useQuery({
    queryKey: ['me'],
    queryFn: async () => (await api.get('/me')).data,
  });

export type ClientsListParams = {
  q?: string;
  tag_id?: string;
  sort?: string;
  order?: string;
  inactive_days?: number;
  page?: number;
  page_size?: number;
};

export type ClientsListPage = {
  total: number;
  page: number;
  page_size: number;
  items: any[];
};

export const CLIENTS_PAGE_SIZE = 50;

export const useClients = (params?: ClientsListParams) =>
  useQuery({
    queryKey: ['clients', params, scopeParam()],
    queryFn: async () => {
      const { data } = await api.get('/clients', {
        params: { ...params, master_id: scopeParam() },
      });
      return data as ClientsListPage;
    },
  });

export const useClientsInfinite = (params?: Omit<ClientsListParams, 'page'>) =>
  useInfiniteQuery({
    queryKey: ['clients', 'infinite', params, scopeParam()],
    queryFn: async ({ pageParam }) => {
      const page_size = params?.page_size ?? CLIENTS_PAGE_SIZE;
      const { data } = await api.get('/clients', {
        params: {
          ...params,
          master_id: scopeParam(),
          page: pageParam,
          page_size,
        },
      });
      return data as ClientsListPage;
    },
    initialPageParam: 1,
    getNextPageParam: (last) => {
      const loaded = last.page * last.page_size;
      return loaded < last.total ? last.page + 1 : undefined;
    },
  });

export const useClient = (id: string | undefined) =>
  useQuery({
    queryKey: ['client', id],
    queryFn: async () => (await api.get(`/clients/${id}`)).data,
    enabled: !!id,
  });

export const useAppointments = (params?: {
  from?: string;
  to?: string;
  client_id?: string;
  status?: string;
}) =>
  useQuery({
    queryKey: ['appointments', params, scopeParam()],
    queryFn: async () => {
      const { data } = await api.get('/appointments', {
        params: { ...params, master_id: scopeParam() },
      });
      return data;
    },
  });

export const useAppointment = (id: string | undefined) =>
  useQuery({
    queryKey: ['appointment', id],
    queryFn: async () => (await api.get(`/appointments/${id}`)).data,
    enabled: !!id,
  });

export const useServices = () =>
  useQuery({
    queryKey: ['services'],
    queryFn: async () => (await api.get('/settings/services')).data,
    staleTime: 5 * 60_000,
  });

export const useExtras = () =>
  useQuery({
    queryKey: ['extras'],
    queryFn: async () => (await api.get('/settings/extras')).data,
    staleTime: 5 * 60_000,
  });

export const useLengths = () =>
  useQuery({
    queryKey: ['lengths'],
    queryFn: async () => (await api.get('/settings/lengths')).data,
    staleTime: 5 * 60_000,
  });

export const useTags = () =>
  useQuery({
    queryKey: ['tags'],
    queryFn: async () => (await api.get('/settings/tags')).data,
    staleTime: 5 * 60_000,
  });

export const useExpenseCategories = () =>
  useQuery({
    queryKey: ['expense_categories'],
    queryFn: async () => (await api.get('/settings/expense-categories')).data,
    staleTime: 5 * 60_000,
  });

export const useStats = (params: {
  from: string;
  to: string;
  master_id?: string;
  service_id?: string;
}) =>
  useQuery({
    queryKey: ['stats', params, scopeParam()],
    queryFn: async () => {
      const { data } = await api.get('/stats', {
        params: { ...params, master_id: params.master_id ?? scopeParam() },
      });
      return data;
    },
  });

export const useStock = () =>
  useQuery({
    queryKey: ['stock'],
    queryFn: async () => (await api.get('/hair-stock')).data,
  });

export const useExpenses = (params?: { from?: string; to?: string }) =>
  useQuery({
    queryKey: ['expenses', params, scopeParam()],
    queryFn: async () => {
      const { data } = await api.get('/expenses', {
        params: { ...params, master_id: scopeParam() },
      });
      return data;
    },
  });

export const useOtherIncomes = (params?: { from?: string; to?: string }) =>
  useQuery({
    queryKey: ['other_incomes', params, scopeParam()],
    queryFn: async () => {
      const { data } = await api.get('/other-incomes', {
        params: { ...params, master_id: scopeParam() },
      });
      return data;
    },
  });

export const useMasters = () =>
  useQuery({
    queryKey: ['masters'],
    queryFn: async () => (await api.get('/settings/masters')).data,
    staleTime: 5 * 60_000,
  });

export const useInvalidateAll = () => {
  const qc = useQueryClient();
  return () => {
    qc.invalidateQueries({ queryKey: ['appointments'] });
    qc.invalidateQueries({ queryKey: ['clients'] });
    qc.invalidateQueries({ queryKey: ['stats'] });
    qc.invalidateQueries({ queryKey: ['other_incomes'] });
  };
};
